import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layout/ClientLayout';
import ClientOnboarding from '../../components/ClientOnboarding';
import ProgressionDashboard from '../../components/ProgressionDashboard';
import StrengthProgressMessage from '../../components/StrengthProgressMessage';
import RestOnboarding from '../../components/RestOnboarding';
import UpdateRestBanner from '../../components/UpdateRestBanner';
import GuardianBlock from '../../components/GuardianBlock';
import MesocycleReport from '../../components/MesocycleReport';
import MesocycleReadyModal from '../../components/MesocycleReadyModal';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  ChevronRight, 
  Loader2, 
  Play, 
  Video, 
  MessageCircleQuestion, 
  Send, 
  CheckCircle2, 
  Clock, 
  Trash2,
  Upload,
  Dumbbell,
  Lightbulb,
  BookOpen,
  ChevronDown,
  ChevronUp,
  GraduationCap,
  Brain,
  Heart,
  Target,
  Trophy,
  FileBarChart,
  Tag,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Onboarding state
  const [onboardingCompleted, setOnboardingCompleted] = useState(null); // null = loading, true/false
  
  // Rest onboarding state
  const [showRestOnboarding, setShowRestOnboarding] = useState(false);
  
  // Daily tip
  const [dailyTip, setDailyTip] = useState(null);
  const [tipExpanded, setTipExpanded] = useState(false);
  
  // Trainer video
  const [trainerVideo, setTrainerVideo] = useState(null);
  
  // Consultations
  const [consultations, setConsultations] = useState([]);
  const [showNewConsultation, setShowNewConsultation] = useState(false);
  const [newConsultation, setNewConsultation] = useState({ video_url: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // Pending tips
  const [pendingTips, setPendingTips] = useState([]);
  const [expandedPendingTip, setExpandedPendingTip] = useState(null);
  
  // Daily note from trainer
  const [dailyNote, setDailyNote] = useState(null);
  
  // Píldora Progresiva Individual
  const [pildoraProgresiva, setPildoraProgresiva] = useState(null);
  const [pildoraExpanded, setPildoraExpanded] = useState(false);
  const [completingPildora, setCompletingPildora] = useState(false);
  
  // Mesocycle Report
  const [showMesocycleReport, setShowMesocycleReport] = useState(false);
  const [mesocycleProgress, setMesocycleProgress] = useState(null);

  useEffect(() => {
    checkOnboarding();
    fetchPlans();
    fetchTrainerVideo();
    fetchConsultations();
    fetchPendingTips();
    fetchDailyNote();
    fetchPildoraProgresiva();
    fetchMesocycleProgress();
  }, []);

  const fetchMesocycleProgress = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-full-report`);
      setMesocycleProgress(response.data);
    } catch (error) {
      console.error('Error fetching mesocycle progress:', error);
    }
  };

  const checkOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/me/onboarding-status`);
      setOnboardingCompleted(response.data.completed);
    } catch (error) {
      console.error('Failed to check onboarding:', error);
      setOnboardingCompleted(true); // En caso de error, asumir completado
    }
  };

  const handleOnboardingComplete = () => {
    setOnboardingCompleted(true);
  };

  const handleRestOnboardingComplete = (program) => {
    setShowRestOnboarding(false);
    toast.success(`Programa de descanso configurado: ${program?.replace('_', ' ')}`);
  };

  const handleStartRestOnboarding = () => {
    setShowRestOnboarding(true);
  };

  const fetchPendingTips = async () => {
    try {
      const response = await axios.get(`${API}/me/pending-tips`);
      setPendingTips(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending tips:', error);
    }
  };

  const fetchDailyNote = async () => {
    try {
      const response = await axios.get(`${API}/me/daily-note`);
      setDailyNote(response.data);
    } catch (error) {
      console.error('Failed to fetch daily note:', error);
    }
  };

  const fetchPildoraProgresiva = async () => {
    try {
      const response = await axios.get(`${API}/me/pildora-progresiva-actual`);
      setPildoraProgresiva(response.data);
    } catch (error) {
      console.error('Failed to fetch pildora progresiva:', error);
    }
  };

  const completarPildora = async () => {
    setCompletingPildora(true);
    try {
      const response = await axios.post(`${API}/me/completar-pildora`);
      toast.success(response.data.message);
      fetchPildoraProgresiva();
      setPildoraExpanded(false);
    } catch (error) {
      toast.error('Error al completar píldora');
    } finally {
      setCompletingPildora(false);
    }
  };

  const getCategoriaIcon = (categoria) => {
    switch (categoria?.toUpperCase()) {
      case 'CUERPO':
        return <Heart className="h-4 w-4" />;
      case 'MENTE':
        return <Brain className="h-4 w-4" />;
      case 'PROPOSITO':
        return <Target className="h-4 w-4" />;
      default:
        return <GraduationCap className="h-4 w-4" />;
    }
  };

  const getCategoriaColor = (categoria) => {
    switch (categoria?.toUpperCase()) {
      case 'CUERPO':
        return 'text-red-400 bg-red-500/20';
      case 'MENTE':
        return 'text-purple-400 bg-purple-500/20';
      case 'PROPOSITO':
        return 'text-amber-400 bg-amber-500/20';
      default:
        return 'text-primary bg-primary/20';
    }
  };

  const markTipAsRead = async (tipId) => {
    try {
      await axios.post(`${API}/me/mark-tip-read/${tipId}`);
      toast.success('Píldora marcada como leída');
      setPendingTips(pendingTips.filter(t => t.id !== tipId));
      setExpandedPendingTip(null);
    } catch (error) {
      toast.error('Error al marcar como leída');
    }
  };

  const fetchDailyTip = async () => {
    try {
      const response = await axios.get(`${API}/tips/today`);
      setDailyTip(response.data);
    } catch (error) {
      console.error('Failed to fetch daily tip:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainerVideo = async () => {
    try {
      const response = await axios.get(`${API}/me/trainer-video`);
      setTrainerVideo(response.data);
    } catch (error) {
      console.error('Failed to fetch trainer video:', error);
    }
  };

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API}/consultations`);
      setConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    }
  };

  const submitConsultation = async () => {
    if (!newConsultation.video_url || !newConsultation.description) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, newConsultation);
      toast.success('Consulta enviada correctamente');
      setNewConsultation({ video_url: '', description: '' });
      setShowNewConsultation(false);
      fetchConsultations();
    } catch (error) {
      toast.error('Error al enviar consulta');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteConsultation = async (consultationId) => {
    try {
      await axios.delete(`${API}/consultations/${consultationId}`);
      toast.success('Consulta eliminada');
      fetchConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs"><Clock className="h-3 w-3" /> Pendiente</span>;
      case 'reviewed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs"><MessageCircleQuestion className="h-3 w-3" /> Revisado</span>;
      case 'resolved':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs"><CheckCircle2 className="h-3 w-3" /> Resuelto</span>;
      default:
        return null;
    }
  };

  // Helper to get video embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // Loom
    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) {
      return `https://www.loom.com/embed/${loomMatch[1]}`;
    }
    
    // Google Drive
    const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    if (driveMatch) {
      return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
    }
    
    return url;
  };

  // Mostrar onboarding si no está completado
  if (onboardingCompleted === null) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ClientLayout>
    );
  }

  if (onboardingCompleted === false) {
    return <ClientOnboarding onComplete={handleOnboardingComplete} />;
  }

  // Mostrar Rest Onboarding si el usuario lo solicita
  if (showRestOnboarding) {
    return (
      <RestOnboarding 
        onComplete={handleRestOnboardingComplete}
        onSkip={() => setShowRestOnboarding(false)}
      />
    );
  }

  return (
    <ClientLayout>
      {/* Banner para actualizar onboarding de descanso */}
      <UpdateRestBanner onStartOnboarding={handleStartRestOnboarding} />
      
      {/* Mensaje de progreso automático (3+ semanas) */}
      <StrengthProgressMessage />
      
      <div className="space-y-6 py-6 animate-fade-in" data-testid="client-dashboard">
        {/* Welcome */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">{t('myTraining')}</h1>
          <p className="text-muted-foreground mt-1">{t('welcomeBack')}, {user?.name}</p>
        </div>

        {/* CTA Planes y Precios — visible en móvil sin abrir menú */}
        <div className="grid grid-cols-2 gap-3" data-testid="pricing-cta-grid">
          <button
            type="button"
            onClick={() => navigate('/pricing')}
            data-testid="pricing-cta-card"
            className="text-left rounded-lg border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent hover:from-primary/20 hover:border-primary/50 transition-all p-3 active:scale-95"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-wide">Planes</span>
            </div>
            <p className="text-sm font-semibold text-foreground leading-tight">Ver planes y precios</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">14-30 días gratis</p>
          </button>
          <button
            type="button"
            onClick={() => navigate('/portal/suscripcion')}
            data-testid="subscription-cta-card"
            className="text-left rounded-lg border border-zinc-700/50 bg-zinc-900/50 hover:border-zinc-600 hover:bg-zinc-800/50 transition-all p-3 active:scale-95"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Tag className="h-4 w-4 text-zinc-400" />
              <span className="text-xs font-bold text-zinc-400 uppercase tracking-wide">Mi cuenta</span>
            </div>
            <p className="text-sm font-semibold text-foreground leading-tight">Mi suscripción</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Gestionar</p>
          </button>
        </div>

        {/* 📝 NOTA DIARIA DEL ENTRENADOR */}
        {dailyNote && (
          <Card className="bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent border-blue-500/30" data-testid="daily-note-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-blue-500/20 flex-shrink-0">
                  <MessageCircleQuestion className="h-5 w-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-blue-400 font-medium uppercase tracking-wider mb-2">
                    📝 Nota de tu entrenador
                  </p>
                  <p className="text-sm text-foreground">{dailyNote.content}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trainer Video Section */}
        {trainerVideo?.video_url && (
          <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20" data-testid="trainer-video-card">
            <div className="p-4 pb-2">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Play className="h-5 w-5 text-primary" />
                {trainerVideo.title || 'Mensaje de tu entrenador'}
              </h3>
            </div>
            <CardContent>
              {getEmbedUrl(trainerVideo.video_url) ? (
                <div className="aspect-video rounded-lg overflow-hidden bg-black">
                  <iframe
                    src={getEmbedUrl(trainerVideo.video_url)}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video del entrenador"
                  />
                </div>
              ) : (
                <div className="aspect-video rounded-lg bg-zinc-900 flex flex-col items-center justify-center">
                  <Video className="h-8 w-8 text-zinc-500 mb-2" />
                  <p className="text-zinc-400 text-sm">Video no disponible</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Go to Current Session - Main CTA */}
        <Card 
          className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border-primary/30 cursor-pointer card-hover"
          onClick={() => navigate('/portal/session')}
          data-testid="go-to-session-card"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-lg bg-primary/20">
                  <Dumbbell className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">Mi Sesión de Hoy</h3>
                  <p className="text-muted-foreground mt-1">
                    Accede a tu entrenamiento actual
                  </p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6 text-primary" />
            </div>
          </CardContent>
        </Card>

        {/* 🛡️ OBJETIVO GUARDIÁN */}
        <GuardianBlock />

        {/* 📊 BANNER FINALIZAR MESOCICLO - Aparece al 80% */}
        {mesocycleProgress?.can_finalize && (
          <Card 
            className="bg-gradient-to-r from-primary/20 via-amber-500/20 to-primary/20 border-primary/50 cursor-pointer hover:border-primary transition-all animate-pulse-slow"
            onClick={() => setShowMesocycleReport(true)}
            data-testid="finalize-mesocycle-banner"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-gradient-to-br from-primary to-amber-500">
                    <Trophy className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">
                      ¡Mesociclo {mesocycleProgress.mesocycle} Listo para Finalizar!
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {mesocycleProgress.progress_percent}% completado • Toca para ver tu informe
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FileBarChart className="h-5 w-5 text-primary" />
                  <ChevronRight className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 📊 DASHBOARD DE PROGRESIÓN */}
        <ProgressionDashboard />

        {/* 🎓 PÍLDORA PROGRESIVA INDIVIDUAL */}
        {pildoraProgresiva?.pildora && (
          <Card className="bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-transparent border-emerald-500/30" data-testid="pildora-progresiva-card">
            <CardContent className="p-4">
              {/* Header con progreso */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-emerald-500/20">
                    <GraduationCap className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      Tu Formación
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Píldora #{pildoraProgresiva.progreso.pildora_actual} de {pildoraProgresiva.progreso.total_pildoras}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-emerald-400">
                    {pildoraProgresiva.progreso.porcentaje}%
                  </span>
                </div>
              </div>
              
              {/* Barra de progreso */}
              <Progress value={pildoraProgresiva.progreso.porcentaje} className="h-2 mb-4 bg-emerald-950" />
              
              {/* Píldora actual */}
              <div className="bg-black/20 rounded-lg overflow-hidden">
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setPildoraExpanded(!pildoraExpanded)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${getCategoriaColor(pildoraProgresiva.pildora.categoria)}`}>
                          {getCategoriaIcon(pildoraProgresiva.pildora.categoria)}
                          {pildoraProgresiva.pildora.categoria}
                        </span>
                      </div>
                      <h4 className="font-bold text-foreground">
                        {pildoraProgresiva.pildora.titulo}
                      </h4>
                      {!pildoraExpanded && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {pildoraProgresiva.pildora.contenido.replace(/\*\*/g, '').substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="text-emerald-400 flex-shrink-0">
                      {pildoraExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </Button>
                  </div>
                </div>
                
                {/* Contenido expandido */}
                {pildoraExpanded && (
                  <div className="px-4 pb-4 space-y-4 animate-fade-in border-t border-emerald-500/20">
                    <div className="prose prose-invert prose-sm max-w-none pt-4">
                      {pildoraProgresiva.pildora.contenido.split('\n\n').map((paragraph, i) => (
                        <p key={i} className="text-sm text-foreground/90 mb-3 leading-relaxed">
                          {paragraph.split('**').map((part, j) => 
                            j % 2 === 1 ? <strong key={j} className="text-emerald-300">{part}</strong> : part
                          )}
                        </p>
                      ))}
                    </div>
                    
                    {/* Referencias */}
                    {pildoraProgresiva.pildora.referencias && (
                      <div className="pt-3 border-t border-emerald-500/20">
                        <p className="flex items-start gap-2 text-xs text-muted-foreground">
                          <BookOpen className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span className="italic">{pildoraProgresiva.pildora.referencias}</span>
                        </p>
                      </div>
                    )}
                    
                    {/* Botón completar */}
                    <Button 
                      onClick={completarPildora}
                      disabled={completingPildora}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {completingPildora ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      He leído y entendido esta píldora
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 💊 PÍLDORAS PENDIENTES */}
        {pendingTips.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border-amber-500/30" data-testid="pending-tips-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-amber-500/20">
                    <Lightbulb className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      💊 Píldoras Pendientes
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pendingTips.length} {pendingTips.length === 1 ? 'píldora' : 'píldoras'} por leer
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                {pendingTips.map((tip) => (
                  <div key={tip.id} className="bg-black/20 rounded-lg overflow-hidden">
                    <div 
                      className="p-3 cursor-pointer flex items-center justify-between"
                      onClick={() => setExpandedPendingTip(expandedPendingTip === tip.id ? null : tip.id)}
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground text-sm">{tip.title}</h4>
                        {expandedPendingTip !== tip.id && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {tip.content.replace(/\*\*/g, '').substring(0, 80)}...
                          </p>
                        )}
                      </div>
                      <Button variant="ghost" size="icon" className="text-amber-400 flex-shrink-0">
                        {expandedPendingTip === tip.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                    
                    {expandedPendingTip === tip.id && (
                      <div className="px-3 pb-3 space-y-3 animate-fade-in">
                        <div className="prose prose-invert prose-sm max-w-none border-l-2 border-amber-500/30 pl-3">
                          {tip.content.split('\n\n').map((paragraph, i) => (
                            <p key={i} className="text-xs text-foreground/90 mb-2">
                              {paragraph.split('**').map((part, j) => 
                                j % 2 === 1 ? <strong key={j} className="text-amber-300">{part}</strong> : part
                              )}
                            </p>
                          ))}
                        </div>
                        
                        {tip.references && (
                          <div className="pt-2 border-t border-amber-500/20">
                            <p className="flex items-start gap-2 text-xs text-muted-foreground">
                              <BookOpen className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="italic line-clamp-2">{tip.references}</span>
                            </p>
                          </div>
                        )}
                        
                        <Button 
                          onClick={() => markTipAsRead(tip.id)}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-sm"
                          size="sm"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Marcar como leída
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans section REMOVED - Clients only see "Session of the Day" */}

        {/* Video Consultations Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <MessageCircleQuestion className="h-5 w-5 text-blue-400" />
              Mis Consultas
            </h2>
            <Dialog open={showNewConsultation} onOpenChange={setShowNewConsultation}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                  <Upload className="h-4 w-4 mr-2" />
                  Subir Video
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Video className="h-5 w-5 text-blue-400" />
                    Nueva Consulta con Video
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">
                      URL del video (YouTube, Loom, Google Drive, etc.)
                    </label>
                    <Input
                      value={newConsultation.video_url}
                      onChange={(e) => setNewConsultation(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://..."
                      className="bg-zinc-900/50 border-zinc-800"
                    />
                    <p className="text-xs text-muted-foreground">
                      💡 Graba tu ejercicio con el móvil, súbelo a YouTube/Google Drive y pega el enlace aquí
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">
                      Describe tu duda o lo que quieres que revise
                    </label>
                    <Textarea
                      value={newConsultation.description}
                      onChange={(e) => setNewConsultation(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Ej: No sé si estoy bajando bien en la sentadilla..."
                      className="bg-zinc-900/50 border-zinc-800 min-h-[100px]"
                    />
                  </div>
                  <Button 
                    onClick={submitConsultation} 
                    disabled={submitting}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Enviar Consulta
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {consultations.length === 0 ? (
            <Card className="bg-card border-border border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <MessageCircleQuestion className="h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">No tienes consultas</p>
                <p className="text-xs text-muted-foreground mt-1">¿Tienes dudas sobre algún ejercicio? ¡Sube un video!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {consultations.map((consultation) => (
                <Card key={consultation.id} className="bg-card border-border" data-testid={`consultation-${consultation.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(consultation.status)}
                          <span className="text-xs text-muted-foreground">
                            {new Date(consultation.created_at).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                        <p className="text-sm text-foreground">{consultation.description}</p>
                        
                        {/* Video preview */}
                        {consultation.video_url && (
                          <a 
                            href={consultation.video_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline mt-2"
                          >
                            <Video className="h-3 w-3" />
                            Ver mi video
                          </a>
                        )}
                        
                        {/* Trainer response */}
                        {consultation.trainer_response && (
                          <div className="mt-3 p-3 bg-primary/10 rounded-sm border-l-2 border-primary">
                            <p className="text-xs text-primary font-semibold mb-1">Respuesta del entrenador:</p>
                            <p className="text-sm text-foreground">{consultation.trainer_response}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Delete button - only for resolved */}
                      {consultation.status === 'resolved' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteConsultation(consultation.id)}
                          className="text-destructive h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de Informe de Mesociclo */}
      <Dialog open={showMesocycleReport} onOpenChange={setShowMesocycleReport}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700 p-0">
          <MesocycleReport 
            onClose={() => setShowMesocycleReport(false)}
            onAdvance={() => {
              setShowMesocycleReport(false);
              fetchMesocycleProgress();
              toast.success('¡Bienvenido al nuevo mesociclo!');
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Modal centrado cuando completan 4 semanas */}
      <MesocycleReadyModal onShowReport={() => setShowMesocycleReport(true)} />
    </ClientLayout>
  );
};

export default ClientDashboard;
